import {CommonModule} from '@angular/common';
import {Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {catchError, map, of, switchMap, tap} from 'rxjs';
import {TrendGranularity} from '../../dashboard-analytics/models/dashboard-statistics.model';
import {DashboardStatisticsService, UploadedPureOriginalPage} from '../../dashboard-analytics/services/dashboard-statistics.service';
import {DashboardPeriodService} from '../../dashboard/services/dashboard-period.service';
import {PureOriginalsModel} from '../../models/PureOriginals-model';

@Component({
  selector: 'app-uploaded-pure-originals',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './uploaded-pure-originals.component.html',
  styleUrls: ['./uploaded-pure-originals.component.css']
})
export class UploadedPureOriginalsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly statsService = inject(DashboardStatisticsService);
  private readonly periodService = inject(DashboardPeriodService);
  private readonly destroyRef = inject(DestroyRef);

  readonly page = signal<UploadedPureOriginalPage | null>(null);
  readonly loading = signal(false);
  readonly error = signal(false);
  readonly pageNumber = signal(0);
  readonly pageSize = signal(50);
  readonly rangeLabel = signal('');

  ngOnInit(): void {
    this.route.queryParamMap.pipe(
      map(params => {
        const fallback = this.periodService.pureOriginalRange();
        const startDate = params.get('startDate') || fallback.start;
        const endDate = params.get('endDate') || fallback.end;
        const requestedGranularity = params.get('granularity');
        const granularity: TrendGranularity = requestedGranularity === 'WEEK' || requestedGranularity === 'MONTH'
          ? requestedGranularity : 'DAY';
        const requestedPage = Number(params.get('pageNumber'));
        const requestedSize = Number(params.get('pageSize'));
        return {
          startDate,
          endDate,
          granularity,
          pageNumber: Number.isInteger(requestedPage) && requestedPage >= 0 ? requestedPage : 0,
          pageSize: Number.isInteger(requestedSize) && requestedSize >= 1 && requestedSize <= 100 ? requestedSize : 50
        };
      }),
      tap(({startDate, endDate, pageNumber, pageSize}) => {
        this.loading.set(true);
        this.error.set(false);
        this.page.set(null);
        this.pageNumber.set(pageNumber);
        this.pageSize.set(pageSize);
        this.rangeLabel.set(`${startDate.replace('T', ' ')} – ${endDate.replace('T', ' ')}`);
      }),
      switchMap(({startDate, endDate, granularity, pageNumber, pageSize}) =>
        this.statsService.getUploadedPureOriginals(startDate, endDate, granularity, pageNumber, pageSize).pipe(
          catchError(() => {
            this.error.set(true);
            return of(null);
          })
        )
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(page => {
      this.page.set(page);
      this.loading.set(false);
    });
  }

  goToPage(pageNumber: number): void {
    if (pageNumber < 0 || this.loading()) return;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {pageNumber},
      queryParamsHandling: 'merge'
    });
  }

  changePageSize(event: Event): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {pageNumber: 0, pageSize: Number((event.target as HTMLSelectElement).value)},
      queryParamsHandling: 'merge'
    });
  }

  openDetails(row: PureOriginalsModel): void {
    this.router.navigate(['/app/pure-originals/pure-original-details'], {
      queryParams: {pureOriginalId: row.id}
    });
  }
}
